import React, { useState, useEffect, useRef } from 'react';
import TextEdit from '../../../molecules/TextEdit';
import { Auth } from 'aws-amplify';
import appConfig from '../../../../config/app-config';

const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;
const { address: GEOLOCATION_API_URL, key: GEOLOCATION_API_KEY } = appConfig.apis.geolocationApiEnv;

export interface AssignorBillingAddressData {
    name: string;
    document_number: string;
    address_street: string;
    address_number: string;
    address_complement: string;
    address_neighborhood: string;
    address_city: string;
    address_state: string;
    address_zip_code: string;
}

export const EMPTY_ASSIGNOR_BILLING_ADDRESS: AssignorBillingAddressData = {
    name: '',
    document_number: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_neighborhood: '',
    address_city: '',
    address_state: '',
    address_zip_code: '',
};

interface Props {
    value: AssignorBillingAddressData;
    onChange: (data: AssignorBillingAddressData) => void;
    disabled?: boolean;
    assignorPersonId?: string;
    assignorName?: string;
    assignorDocument?: string;
}

const fetchCep = async (cep: string) => {
    const clean = cep.replace(/\D/g, '');
    if (clean.length !== 8) return null;
    try {
        const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
        const data = await res.json();
        if (data.erro) return null;
        return data;
    } catch {
        return null;
    }
};

const AssignorBillingAddress: React.FC<Props> = ({ value, onChange, disabled = false, assignorPersonId, assignorName, assignorDocument }) => {
    const [loadingCep, setLoadingCep] = useState(false);
    const [loadingAddress, setLoadingAddress] = useState(false);
    const lastFetchedId = useRef<string>('');

    // Pre-fill name and document from assignor props
    useEffect(() => {
        if (!assignorName && !assignorDocument) return;
        const needsUpdate = (assignorName && value.name !== assignorName) || (assignorDocument && value.document_number !== assignorDocument);
        if (!needsUpdate) return;
        onChange({ ...value, name: assignorName || value.name, document_number: assignorDocument || value.document_number });
    }, [assignorName, assignorDocument]);

    // Fetch address from Person API only when address is empty (not loaded from DB)
    useEffect(() => {
        if (!assignorPersonId || assignorPersonId === '00000000-0000-0000-0000-000000000000') return;
        if (value.address_street) return; // Already has address from DB payload
        if (!value.name) return; // Wait until contract data is loaded (name comes from DB)
        if (lastFetchedId.current === assignorPersonId) return;

        lastFetchedId.current = assignorPersonId;
        const fetchAssignorAddress = async () => {
            setLoadingAddress(true);
            try {
                const token = await Auth.currentSession().then(s => s.getIdToken().getJwtToken());
                const res = await fetch(`${PERSON_API_URL}/person/${assignorPersonId}/addresses`, {
                    headers: { 'x-api-key': PERSON_API_KEY, 'Authorization': `Bearer ${token}` }
                });
                if (!res.ok) return;
                const data = await res.json();
                const addresses = data.addresses || data || [];
                if (addresses.length === 0) return;
                const addr = addresses[0];

                let cityName = '';
                let stateName = '';
                if (addr.cityId) {
                    try {
                        const cityRes = await fetch(`${GEOLOCATION_API_URL}/cities/${addr.cityId}?fields=id,name,province.initials`, {
                            headers: { 'x-api-key': GEOLOCATION_API_KEY }
                        });
                        if (cityRes.ok) {
                            const cityData = await cityRes.json();
                            cityName = cityData.name || '';
                            stateName = cityData.province?.initials || '';
                        }
                    } catch { /* silent */ }
                }

                onChange({
                    name: assignorName || value.name,
                    document_number: assignorDocument || value.document_number,
                    address_street: addr.street || '',
                    address_number: addr.number || '',
                    address_complement: addr.complement || '',
                    address_neighborhood: addr.district || '',
                    address_city: cityName,
                    address_state: stateName,
                    address_zip_code: addr.zipcode || '',
                });
            } catch { /* silent */ }
            finally { setLoadingAddress(false); }
        };
        fetchAssignorAddress();
    }, [assignorPersonId, value.address_street, value.name]);

    const update = (field: keyof AssignorBillingAddressData, val: string) => {
        onChange({ ...value, [field]: val });
    };

    const handleCepBlur = async () => {
        const clean = value.address_zip_code.replace(/\D/g, '');
        if (clean.length !== 8) return;
        setLoadingCep(true);
        const data = await fetchCep(clean);
        if (data) {
            onChange({
                ...value,
                address_street: data.logradouro || value.address_street,
                address_neighborhood: data.bairro || value.address_neighborhood,
                address_city: data.localidade || value.address_city,
                address_state: data.uf || value.address_state,
            });
        }
        setLoadingCep(false);
    };

    return (
        <div className="border border-gray-200 dark:border-slate-600 rounded-lg p-4 mt-4">
            <h3 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-200">
                Endereço de Faturamento do Cedente
            </h3>
            {loadingAddress ? (
                <div className="flex items-center justify-center py-8 gap-3">
                    <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    <span className="text-sm text-blue-500 dark:text-blue-400">Localizando endereço...</span>
                </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TextEdit
                    id="billing-name"
                    label="Nome / Razão Social *"
                    value={value.name}
                    onChange={(e) => update('name', e.target.value)}
                    disabled={true}
                />
                <TextEdit
                    id="billing-document"
                    label="CPF / CNPJ *"
                    value={value.document_number}
                    onChange={(e) => update('document_number', e.target.value)}
                    disabled={true}
                />
                <div className="relative">
                    <TextEdit
                        id="billing-zip"
                        label="CEP *"
                        value={value.address_zip_code}
                        onChange={(e) => update('address_zip_code', e.target.value)}
                        onBlur={handleCepBlur}
                        disabled={disabled}
                        maxLength={9}
                        placeholder="00000-000"
                    />
                    {loadingCep && (
                        <span className="absolute right-2 top-8 text-xs text-blue-500">Buscando...</span>
                    )}
                </div>
                <TextEdit
                    id="billing-street"
                    label="Logradouro *"
                    value={value.address_street}
                    onChange={(e) => update('address_street', e.target.value)}
                    disabled={disabled}
                />
                <TextEdit
                    id="billing-number"
                    label="Número *"
                    value={value.address_number}
                    onChange={(e) => update('address_number', e.target.value)}
                    disabled={disabled}
                />
                <TextEdit
                    id="billing-complement"
                    label="Complemento"
                    value={value.address_complement}
                    onChange={(e) => update('address_complement', e.target.value)}
                    disabled={disabled}
                />
                <TextEdit
                    id="billing-neighborhood"
                    label="Bairro *"
                    value={value.address_neighborhood}
                    onChange={(e) => update('address_neighborhood', e.target.value)}
                    disabled={disabled}
                />
                <TextEdit
                    id="billing-city"
                    label="Cidade *"
                    value={value.address_city}
                    onChange={(e) => update('address_city', e.target.value)}
                    disabled={disabled}
                />
                <TextEdit
                    id="billing-state"
                    label="UF *"
                    value={value.address_state}
                    onChange={(e) => update('address_state', e.target.value.toUpperCase())}
                    disabled={disabled}
                    maxLength={2}
                    placeholder="SP"
                />
            </div>
            )}
        </div>
    );
};

export default AssignorBillingAddress;
